<template>
  <div>
    <b-modal id="importTeamsV2" size="lg" @shown="onShown" @hidden="clearData">
      <div slot="modal-header">
        <h2>{{ $t("import_teamsv2.modal_title", "Import teams") }}</h2>
        <span class="import-v2__step-badge">{{ stepBadge }}</span>
        <div class="modal-header__hide" @click.prevent="hideModal">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
            ></path>
            <path d="M0 0h24v24H0z" fill="none"></path>
          </svg>
        </div>
      </div>

      <!-- ============================ BODY ============================ -->
      <div class="import-v2">
        <!-- Loading the field schema -->
        <div v-if="loadingSchema" class="import-v2__center">
          <LdsSpinner />
          <p>{{ $t("import_teamsv2.loading_schema", "Loading import fields…") }}</p>
        </div>

        <!-- Schema failed to load -->
        <div v-else-if="schemaError" class="import-v2__center">
          <p class="import-v2__error">{{ schemaError }}</p>
          <button class="bttn bttn--sm bttn--bordered-blue" @click.prevent="fetchSchema">
            {{ $t("import_teamsv2.retry", "Try again") }}
          </button>
        </div>

        <!-- STEP 1 — Upload -->
        <div v-else-if="step === 1">
          <h3 class="import-v2__title">
            {{ $t("import_teamsv2.upload_title", "Upload your teams file") }}
          </h3>
          <p class="import-v2__subtitle">
            {{ $t("import_teamsv2.upload_subtitle", "Charidy supports Excel and CSV files") }}
          </p>

          <div
            class="import-v2__dropzone"
            :class="{ 'import-v2__dropzone--active': dragOver }"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDrop"
            @click="$refs.fileInput.click()"
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#107598"
              stroke-width="1.5"
            >
              <path d="M14 3v4a1 1 0 0 0 1 1h4" />
              <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
              <path d="M12 11v6M9.5 13.5 12 11l2.5 2.5" />
            </svg>
            <p class="import-v2__dropzone-title">
              {{ fileName || $t("import_teamsv2.dropzone_title", "Drag and drop your file here") }}
            </p>
            <p class="import-v2__dropzone-hint">
              {{ $t("import_teamsv2.dropzone_hint", "or click to choose from your computer") }}
            </p>
          </div>
          <input
            ref="fileInput"
            type="file"
            class="import-v2__file-input"
            accept=".xlsx,.csv"
            @change="onFileInputChange"
          />

          <p v-if="readingFile" class="import-v2__hint">
            <LdsSpinner /> {{ $t("import_teamsv2.reading_file", "Reading file…") }}
          </p>
          <p v-if="fileError" class="import-v2__error">{{ fileError }}</p>
        </div>

        <!-- STEP 2 — Map columns -->
        <div v-else-if="step === 2">
          <h3 class="import-v2__title">
            {{ $t("import_teamsv2.map_title", "Map fields to Charidy") }}
          </h3>
          <p class="import-v2__subtitle">
            {{
              $t(
                "import_teamsv2.map_subtitle",
                "Match the columns from your file to the corresponding fields in Charidy"
              )
            }}
          </p>

          <!-- Required fields — always shown so the user can map each one -->
          <div class="import-v2__section">
            <p class="import-v2__section-title">
              {{ $t("import_teamsv2.required_section_title", "Required fields") }}
            </p>
            <p class="import-v2__section-hint">
              {{
                $t(
                  "import_teamsv2.required_section_hint",
                  "These fields are mandatory. Choose which column from your file fills each one."
                )
              }}
            </p>
            <div v-for="field in requiredFields" :key="field.key" class="import-v2__map-group">
              <div
                class="import-v2__map-row import-v2__map-row--required"
                :class="{ 'import-v2__map-row--missing': isFieldUnfilled(field.key) }"
              >
                <div class="import-v2__map-col">
                  <span>{{ field.label }}</span>
                  <span class="import-v2__required-badge">{{
                    $t("import_teamsv2.required_badge", "Required")
                  }}</span>
                </div>
                <span class="import-v2__map-arrow">→</span>

                <!-- Fixed value applied to every row (set globally) -->
                <select
                  v-if="isConstantField(field.key) && field.type === 'bool'"
                  v-model="constants[field.key]"
                  class="import-v2__select"
                >
                  <option value="">
                    {{ $t("import_teamsv2.fixed_value_none", "— No fixed value —") }}
                  </option>
                  <option value="false">{{ $t("import_teamsv2.fixed_value_no", "No") }}</option>
                  <option value="true">{{ $t("import_teamsv2.fixed_value_yes", "Yes") }}</option>
                </select>
                <input
                  v-else-if="isConstantField(field.key)"
                  v-model="constants[field.key]"
                  :type="fixedValueInputType(field.type)"
                  class="import-v2__input"
                  :placeholder="
                    $t('import_teamsv2.fixed_value_placeholder', 'Fixed value for every row')
                  "
                />

                <!-- Map a column from the file (default) -->
                <select
                  v-else
                  class="import-v2__select"
                  :value="getFieldColumn(field.key)"
                  @change="setFieldColumn(field.key, $event.target.value)"
                >
                  <option value="">
                    {{ $t("import_teamsv2.select_column", "— Select a column —") }}
                  </option>
                  <option
                    v-for="col in availableColumns"
                    :key="col.index"
                    :value="col.index"
                    :disabled="isColumnTaken(col.index, field.key)"
                  >
                    {{ col.header || $t("import_teamsv2.column_n", { n: col.index + 1 }) }}
                  </option>
                </select>

                <!-- Merge with another column (only when mapping a column) -->
                <button
                  v-if="
                    !isConstantField(field.key) &&
                    !hasMerge(field.key) &&
                    getFieldColumn(field.key) !== ''
                  "
                  type="button"
                  class="import-v2__merge-toggle"
                  :title="$t('import_teamsv2.add_merge', 'Merge with another column')"
                  @click="startMerge(field.key)"
                >
                  +
                </button>

                <!-- Set one fixed value for every row instead of a column -->
                <button
                  v-if="fieldSupportsConstant(field)"
                  type="button"
                  class="import-v2__const-toggle"
                  :class="{ 'import-v2__const-toggle--active': isConstantField(field.key) }"
                  :title="
                    isConstantField(field.key)
                      ? $t('import_teamsv2.use_column_instead', 'Map a column instead')
                      : $t('import_teamsv2.set_fixed_value', 'Set a fixed value for every row')
                  "
                  @click="toggleConstantField(field.key)"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.6"
                  >
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                  </svg>
                </button>
              </div>
              <div
                v-if="!isConstantField(field.key) && hasMerge(field.key)"
                class="import-v2__merge-row"
              >
                <span class="import-v2__merge-label">
                  {{ $t("import_teamsv2.merge_join", "Join with") }}
                </span>
                <input
                  type="text"
                  class="import-v2__merge-sep"
                  :value="getMergeSeparator(field.key)"
                  :placeholder="$t('import_teamsv2.merge_separator_placeholder', 'separator')"
                  @input="setMergeSeparator(field.key, $event.target.value)"
                />
                <span class="import-v2__merge-label">
                  {{ $t("import_teamsv2.merge_then", "then") }}
                </span>
                <select
                  class="import-v2__select"
                  :value="getMergeColumn(field.key)"
                  @change="setMergeColumn(field.key, $event.target.value)"
                >
                  <option value="">
                    {{ $t("import_teamsv2.select_column", "— Select a column —") }}
                  </option>
                  <option
                    v-for="col in availableColumns"
                    :key="col.index"
                    :value="col.index"
                    :disabled="isColumnTakenForMerge(col.index, field.key)"
                  >
                    {{ col.header || $t("import_teamsv2.column_n", { n: col.index + 1 }) }}
                  </option>
                </select>
                <button
                  type="button"
                  class="import-v2__trash"
                  :title="$t('import_teamsv2.remove_merge', 'Remove merge')"
                  @click="removeMerge(field.key)"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.6"
                  >
                    <path d="M6 6l12 12M18 6l-12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Auto-generate short links -->
          <div v-if="hasShortlinkField" class="import-v2__auto-shortlink">
            <b-form-checkbox v-model="autoGenerateShortlink" switch>
              {{
                $t(
                  "import_teamsv2.auto_generate_shortlink",
                  "Auto-generate short links (numbered sequentially)"
                )
              }}
            </b-form-checkbox>
            <p class="import-v2__hint">
              {{
                $t(
                  "import_teamsv2.auto_generate_shortlink_hint",
                  "The system assigns a unique short link to every team."
                )
              }}
            </p>
          </div>

          <!-- Add country calling code to phone numbers (optional) -->
          <div v-if="phoneFieldMapped" class="import-v2__auto-shortlink">
            <b-form-checkbox v-model="autoPhonePrefix" switch>
              {{ $t("import_teamsv2.auto_phone_prefix", "Add a country code to phone numbers") }}
            </b-form-checkbox>
            <p class="import-v2__hint">
              {{
                $t(
                  "import_teamsv2.auto_phone_prefix_hint",
                  "Drops a single leading 0 and adds the country code below. Numbers already starting with “+” are left as-is."
                )
              }}
            </p>
            <div v-if="autoPhonePrefix" class="import-v2__phone-default">
              <label class="import-v2__phone-default-label">
                {{
                  countryField && getFieldColumn(countryField.key) !== ""
                    ? $t(
                        "import_teamsv2.default_phone_country_fallback",
                        "Fallback country (used when the country column is empty)"
                      )
                    : $t(
                        "import_teamsv2.default_phone_country",
                        "Default country (no country column mapped)"
                      )
                }}
              </label>
              <select v-model="defaultPhoneCountry" class="import-v2__select">
                <option value="">
                  {{ $t("import_teamsv2.default_phone_country_none", "— No default —") }}
                </option>
                <option v-for="c in phoneCountryOptions" :key="c.code" :value="c.code">
                  {{ c.flag }} {{ c.name }} (+{{ c.callingCode }})
                </option>
              </select>
            </div>
            <p class="import-v2__warn">
              {{
                $t(
                  "import_teamsv2.phone_no_validation_warning",
                  "⚠️ Phone numbers are imported exactly as entered — they are not validated, so incorrect numbers will be saved as-is."
                )
              }}
            </p>
          </div>

          <!-- Optional fields — only auto-matched ones show; add others below -->
          <div class="import-v2__section">
            <p class="import-v2__section-title">
              {{ $t("import_teamsv2.optional_section_title", "Optional fields") }}
            </p>
            <p class="import-v2__section-hint">
              {{
                $t(
                  "import_teamsv2.optional_section_hint",
                  "Fields matched from your file appear below. Add any others you want to import."
                )
              }}
            </p>
            <div
              v-for="field in visibleOptionalFields"
              :key="field.key"
              class="import-v2__map-group"
            >
              <div class="import-v2__map-row">
                <div class="import-v2__map-col">{{ field.label }}</div>
                <span class="import-v2__map-arrow">→</span>

                <!-- Fixed value applied to every row (set globally) -->
                <select
                  v-if="isConstantField(field.key) && field.type === 'bool'"
                  v-model="constants[field.key]"
                  class="import-v2__select"
                >
                  <option value="">
                    {{ $t("import_teamsv2.fixed_value_none", "— No fixed value —") }}
                  </option>
                  <option value="false">{{ $t("import_teamsv2.fixed_value_no", "No") }}</option>
                  <option value="true">{{ $t("import_teamsv2.fixed_value_yes", "Yes") }}</option>
                </select>
                <input
                  v-else-if="isConstantField(field.key)"
                  v-model="constants[field.key]"
                  :type="fixedValueInputType(field.type)"
                  class="import-v2__input"
                  :placeholder="
                    $t('import_teamsv2.fixed_value_placeholder', 'Fixed value for every row')
                  "
                />

                <!-- Map a column from the file (default) -->
                <select
                  v-else
                  class="import-v2__select"
                  :value="getFieldColumn(field.key)"
                  @change="setFieldColumn(field.key, $event.target.value)"
                >
                  <option value="">
                    {{ $t("import_teamsv2.select_column", "— Select a column —") }}
                  </option>
                  <option
                    v-for="col in availableColumns"
                    :key="col.index"
                    :value="col.index"
                    :disabled="isColumnTaken(col.index, field.key)"
                  >
                    {{ col.header || $t("import_teamsv2.column_n", { n: col.index + 1 }) }}
                  </option>
                </select>

                <!-- Merge with another column (only when mapping a column) -->
                <button
                  v-if="
                    !isConstantField(field.key) &&
                    !hasMerge(field.key) &&
                    getFieldColumn(field.key) !== ''
                  "
                  type="button"
                  class="import-v2__merge-toggle"
                  :title="$t('import_teamsv2.add_merge', 'Merge with another column')"
                  @click="startMerge(field.key)"
                >
                  +
                </button>

                <!-- Set one fixed value for every row instead of a column -->
                <button
                  v-if="fieldSupportsConstant(field)"
                  type="button"
                  class="import-v2__const-toggle"
                  :class="{ 'import-v2__const-toggle--active': isConstantField(field.key) }"
                  :title="
                    isConstantField(field.key)
                      ? $t('import_teamsv2.use_column_instead', 'Map a column instead')
                      : $t('import_teamsv2.set_fixed_value', 'Set a fixed value for every row')
                  "
                  @click="toggleConstantField(field.key)"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.6"
                  >
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                  </svg>
                </button>

                <!-- Remove this field -->
                <button
                  type="button"
                  class="import-v2__trash"
                  :title="$t('import_teamsv2.remove_field', 'Remove this field')"
                  @click="removeOptionalField(field.key)"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.6"
                  >
                    <path
                      d="M4 7h16M10 11v6M14 11v6M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"
                    />
                  </svg>
                </button>
              </div>
              <div
                v-if="!isConstantField(field.key) && hasMerge(field.key)"
                class="import-v2__merge-row"
              >
                <span class="import-v2__merge-label">
                  {{ $t("import_teamsv2.merge_join", "Join with") }}
                </span>
                <input
                  type="text"
                  class="import-v2__merge-sep"
                  :value="getMergeSeparator(field.key)"
                  :placeholder="$t('import_teamsv2.merge_separator_placeholder', 'separator')"
                  @input="setMergeSeparator(field.key, $event.target.value)"
                />
                <span class="import-v2__merge-label">
                  {{ $t("import_teamsv2.merge_then", "then") }}
                </span>
                <select
                  class="import-v2__select"
                  :value="getMergeColumn(field.key)"
                  @change="setMergeColumn(field.key, $event.target.value)"
                >
                  <option value="">
                    {{ $t("import_teamsv2.select_column", "— Select a column —") }}
                  </option>
                  <option
                    v-for="col in availableColumns"
                    :key="col.index"
                    :value="col.index"
                    :disabled="isColumnTakenForMerge(col.index, field.key)"
                  >
                    {{ col.header || $t("import_teamsv2.column_n", { n: col.index + 1 }) }}
                  </option>
                </select>
                <button
                  type="button"
                  class="import-v2__trash"
                  :title="$t('import_teamsv2.remove_merge', 'Remove merge')"
                  @click="removeMerge(field.key)"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.6"
                  >
                    <path d="M6 6l12 12M18 6l-12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <p v-if="!visibleOptionalFields.length" class="import-v2__hint">
              {{ $t("import_teamsv2.no_optional_fields", "No optional fields added yet.") }}
            </p>
            <b-dropdown
              size="sm"
              variant="outline-primary"
              :text="$t('import_teamsv2.add_field', '+ Add a field')"
              class="import-v2__add"
            >
              <b-dropdown-item
                v-for="field in addableFields"
                :key="field.key"
                @click="addOptionalField(field.key)"
              >
                {{ field.label }}
              </b-dropdown-item>
              <b-dropdown-divider v-if="addableFields.length" />
              <b-dropdown-item @click="startCustomFieldPrompt()">
                {{ $t("import_teamsv2.add_custom_extra_data_field", "+ Custom extra data field…") }}
              </b-dropdown-item>
            </b-dropdown>

            <div v-if="customFieldPromptOpen" class="import-v2__custom-prompt">
              <label class="import-v2__custom-prompt-label">
                {{
                  $t(
                    "import_teamsv2.custom_field_key_label",
                    "Key name (stored on the team as x_KEY)"
                  )
                }}
              </label>
              <div class="import-v2__custom-prompt-row">
                <span class="import-v2__custom-prompt-prefix">x_</span>
                <input
                  ref="customFieldPromptInput"
                  v-model="customFieldPromptKey"
                  class="import-v2__input"
                  maxlength="64"
                  :placeholder="$t('import_teamsv2.custom_field_key_placeholder', 'cohort')"
                  @keydown.enter.prevent="confirmCustomFieldPrompt"
                />
                <button
                  type="button"
                  class="bttn bttn--sm bttn--orange"
                  @click="confirmCustomFieldPrompt"
                >
                  {{ $t("import_teamsv2.custom_field_add", "Add") }}
                </button>
                <button
                  type="button"
                  class="bttn bttn--sm bttn--bordered-blue"
                  @click="cancelCustomFieldPrompt"
                >
                  {{ $t("import_teamsv2.custom_field_cancel", "Cancel") }}
                </button>
              </div>
              <small v-if="customFieldPromptError" class="import-v2__error">
                {{ customFieldPromptError }}
              </small>
            </div>
          </div>

          <!-- Team hierarchy — parent/grandparent group NAMES, created on the fly -->
          <div v-if="hierarchyFields.length" class="import-v2__section">
            <p class="import-v2__section-title">
              {{ $t("import_teamsv2.hierarchy_section_title", "Team hierarchy") }}
            </p>
            <p class="import-v2__section-hint">
              {{
                $t(
                  "import_teamsv2.hierarchy_section_hint",
                  "Map the columns that hold parent / grandparent group names. One team is created per unique name and each row is linked underneath it (grandparent → parent → team)."
                )
              }}
            </p>
            <div v-for="field in hierarchyFields" :key="field.key" class="import-v2__map-group">
              <div class="import-v2__map-row">
                <div class="import-v2__map-col">{{ field.label }}</div>
                <span class="import-v2__map-arrow">→</span>
                <select
                  class="import-v2__select"
                  :value="getFieldColumn(field.key)"
                  @change="setFieldColumn(field.key, $event.target.value)"
                >
                  <option value="">
                    {{ $t("import_teamsv2.select_column", "— Select a column —") }}
                  </option>
                  <option
                    v-for="col in availableColumns"
                    :key="col.index"
                    :value="col.index"
                    :disabled="isColumnTaken(col.index, field.key)"
                  >
                    {{ col.header || $t("import_teamsv2.column_n", { n: col.index + 1 }) }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <CampaignImportTeamsDuplicates
            :check-duplicates.sync="checkDuplicates"
            :match-fields.sync="matchFields"
            :duplicate-action="duplicateAction"
            :mapped-field-keys="providedFieldKeys"
            :duplicates="preview && preview.duplicates ? preview.duplicates : null"
            :busy="busy"
            @update:duplicateAction="onDuplicateActionChange"
          />

          <CampaignImportTeamsSettings
            class="mt-3"
            @update:do_not_send_team_creation_email="(v) => (sendTeamCreationEmail = v ? '1' : '0')"
            @update:do_not_send_new_linked_team_account_email="
              (v) => (sendNewLinkedTeamAccountEmail = v ? '1' : '0')
            "
          />

          <b-form-group
            :label="$t('edit_campaign.import_teams_after_send_label', 'After import send')"
            class="mt-2"
          >
            <b-form-select v-model="afterSend" :options="afterSendOptions"></b-form-select>
          </b-form-group>

          <!-- Local validation problems -->
          <ul v-if="localErrors.length" class="import-v2__error-list">
            <li v-for="(err, i) in localErrors" :key="i">{{ err }}</li>
          </ul>

          <!-- Dry-run preview -->
          <div
            v-if="preview"
            class="import-v2__preview"
            :class="preview.valid ? 'is-valid' : 'is-invalid'"
          >
            <p class="import-v2__preview-head">
              {{
                preview.valid
                  ? $t("import_teamsv2.preview_ready", "Looks good — ready to import")
                  : $t("import_teamsv2.preview_problems", "Please fix the problems below")
              }}
            </p>
            <p>
              {{ $t("import_teamsv2.preview_rows", "Rows to process") }}:
              <strong>{{ preview.rows_parsed }}</strong>
              &nbsp;·&nbsp;
              {{ $t("import_teamsv2.preview_new", "New teams") }}:
              <strong>{{ preview.new_teams }}</strong>
            </p>
            <p v-if="preview.skipped_rows">
              {{ $t("import_teamsv2.preview_skipped", "Rows skipped as duplicates") }}:
              <strong>{{ preview.skipped_rows }}</strong>
            </p>
            <ul v-if="preview.errors && preview.errors.length" class="import-v2__error-list">
              <li v-for="(err, i) in preview.errors" :key="i">{{ err }}</li>
            </ul>
            <p v-if="needsDuplicateAction" class="import-v2__error">
              {{
                $t(
                  "import_teamsv2.dup_choose_action",
                  "Choose what should happen to the duplicate rows before importing."
                )
              }}
            </p>
          </div>

          <p v-if="generalError" class="import-v2__error">{{ generalError }}</p>
        </div>

        <!-- IMPORTING -->
        <div v-else-if="step === 'importing'" class="import-v2__center">
          <LdsSpinner />
          <p>{{ $t("import_teamsv2.importing", "Importing teams…") }} {{ progress }}%</p>
        </div>

        <!-- STEP 3 — Success -->
        <div v-else-if="step === 3" class="import-v2__center">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1aa779"
            stroke-width="1.5"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M8 12.5 11 15.5 16 9.5" />
          </svg>
          <h3 class="import-v2__title">
            {{ $t("import_teamsv2.success_title", "Data uploaded successfully!") }}
          </h3>
          <p v-if="lastValidation">
            {{
              $t("import_teamsv2.success_summary", {
                rows: lastValidation.rows_parsed,
                new: lastValidation.new_teams,
              })
            }}
          </p>
          <p v-if="importCounts">
            {{
              $t(
                "import_teamsv2.dup_counts",
                "New teams: {created}, updated teams: {updated}, skipped rows: {skipped}",
                importCounts
              )
            }}
          </p>
        </div>
      </div>

      <!-- =========================== FOOTER =========================== -->
      <div slot="modal-footer" class="import-v2__footer">
        <button class="bttn bttn--lg bttn--bordered-blue" @click.prevent="hideModal">
          {{
            step === 3
              ? $t("import_teamsv2.close", "Close")
              : $t("edit_campaign.hide_modal", "Cancel")
          }}
        </button>

        <button
          v-if="step === 2"
          class="bttn bttn--lg bttn--bordered-blue"
          @click.prevent="step = 1"
        >
          {{ $t("import_teamsv2.back", "Back") }}
        </button>

        <button
          v-if="step === 1"
          class="bttn bttn--lg bttn--orange"
          :disabled="!fileHeaders.length || readingFile"
          @click.prevent="step = 2"
        >
          {{ $t("import_teamsv2.next_map", "Next: Map fields") }}
        </button>

        <button
          v-if="step === 2 && !preview"
          class="bttn bttn--lg bttn--orange"
          :disabled="busy || localErrors.length > 0"
          @click.prevent="runDryRun"
        >
          {{ $t("import_teamsv2.validate", "Validate") }}
        </button>

        <button
          v-if="step === 2 && preview"
          class="bttn bttn--lg bttn--orange"
          :disabled="busy || !preview.valid || needsDuplicateAction"
          @click.prevent="runImport"
        >
          {{ $t("import_teamsv2.confirm_upload", "Confirm & Upload") }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import urls from "@/constants/urls.js";
import { countryList } from "@/constants/countries.js";
import LdsSpinner from "@/common-components/ui-elements/lds-spinner.vue";
import CampaignImportTeamsSettings from "./campaign-import-teams-settings.vue";
import CampaignImportTeamsDuplicates from "./campaign-import-teams-duplicates.vue";
import { readSpreadsheetAllRows, buildCsv } from "@/utils/readSpreadsheetHeader.js";

export default {
  name: "campaign-import-teams-v2",

  props: ["campaignId"],

  components: {
    LdsSpinner,
    CampaignImportTeamsSettings,
    CampaignImportTeamsDuplicates,
  },

  data() {
    return {
      step: 1, // 1 = upload, 2 = map, "importing", 3 = success

      loadingSchema: false,
      schemaError: "",
      fields: [],

      file: null,
      fileName: "",
      fileHeaders: [],
      fileRows: [], // every data row, used when we rewrite the file
      readingFile: false,
      fileError: "",
      dragOver: false,

      mapping: {}, // { [columnIndex]: fieldKey }
      constants: {}, // { [fieldKey]: value }
      // Optional second source column appended to a field, joined by `separator`.
      // { [fieldKey]: { columnIndex: number, separator: string } }
      merges: {},
      visibleOptionalFieldKeys: [], // optional fields shown as mapping rows
      // Optional fields switched to a single fixed value for every row (set
      // globally) instead of being mapped to a file column.
      constantFieldKeys: [],
      autoGenerateShortlink: true, // server assigns sequential shortlinks

      // Opt-in: when on, phones without a "+" prefix get the country calling
      // code (+CC) added after dropping a leading 0. Off by default.
      autoPhonePrefix: false,
      // Country used by the prefix transform per row: the mapped country
      // column, falling back to this default (seeded from the IP store value).
      defaultPhoneCountry: "",

      // Custom team_extra_data keys ("x_<key>") added by the user. Treated as
      // virtual optional fields so they reuse the same mapping/constants path.
      customFieldKeys: [],

      // Inline prompt for naming a new custom key.
      customFieldPromptOpen: false,
      customFieldPromptKey: "",
      customFieldPromptError: "",

      sendTeamCreationEmail: "",
      sendNewLinkedTeamAccountEmail: "",
      afterSend: null,
      afterSendOptions: [
        {
          text: this.$t(
            "edit_campaign.import_teams_afret_send_nothing",
            "Please select an option or leave empty"
          ),
          value: null,
        },
        {
          text: this.$t("edit_campaign.import_teams_afret_send_email", "Email"),
          value: "email",
        },
        {
          text: this.$t("edit_campaign.import_teams_afret_send_sms", "SMS notification"),
          value: "sms",
        },
      ],

      // Duplicate check: when on, the dry run and the import go to the
      // duplicate-check endpoint, which compares the rows with the campaign's
      // teams on matchFields and applies duplicateAction to the rows found.
      checkDuplicates: false,
      matchFields: ["name"],
      duplicateAction: "", // "", "create", "skip" or "update"
      importCounts: null, // { created, updated, skipped } of a checked import

      busy: false,
      preview: null, // dry-run result
      lastValidation: null,
      generalError: "",

      procID: 0,
      importGroup: null,
      progress: 0,
      interval: null,
    };
  },

  computed: {
    orgId() {
      return localStorage.getItem("orgId");
    },

    resolvedCampaignId() {
      return this.$route.params.id || this.campaignId;
    },

    stepBadge() {
      if (this.step === 1) return this.$t("import_teamsv2.step_1_of_2", "Step 1 of 2");
      if (this.step === 2) return this.$t("import_teamsv2.step_2_of_2", "Step 2 of 2");
      if (this.step === 3) return this.$t("import_teamsv2.completed", "Completed");
      return "";
    },

    // Fields that can be picked in the UI (hide ones disabled for this campaign).
    // Custom team_extra_data keys are appended as virtual optional fields.
    selectableFields() {
      const base = this.fields.filter((f) => f.available !== false);
      return [...base, ...this.customFields];
    },

    // Virtual field definitions for user-added "x_<key>" entries.
    customFields() {
      return this.customFieldKeys.map((key) => ({
        key,
        label: key,
        required: false,
        type: "string",
        available: true,
        supports_constant: true,
        isCustom: true,
      }));
    },

    // Required fields the user must map to a column. `shortlink` is excluded:
    // it is handled exclusively by the auto-generate switch.
    requiredFields() {
      return this.selectableFields.filter((f) => f.required && f.key !== "shortlink");
    },

    // Keys handled by the dedicated "Team hierarchy" section. They carry
    // parent/grandparent group NAMES and create those teams on the fly, so they
    // get their own section instead of the generic optional list.
    hierarchyFieldKeys() {
      return ["parent_team_name", "grandparent_team_name"];
    },

    // Hierarchy fields actually offered by the campaign schema, in schema order.
    hierarchyFields() {
      return this.selectableFields.filter((f) => this.hierarchyFieldKeys.includes(f.key));
    },

    // Every optional field (no required, no `shortlink`, no hierarchy fields).
    optionalFields() {
      return this.selectableFields.filter(
        (f) => !f.required && f.key !== "shortlink" && !this.hierarchyFieldKeys.includes(f.key)
      );
    },

    // Optional fields currently shown as a mapping row — auto-matched ones
    // plus any the user added with the "Add a field" button.
    visibleOptionalFields() {
      return this.optionalFields.filter((f) => this.visibleOptionalFieldKeys.includes(f.key));
    },

    // Optional fields not yet shown — offered in the "Add a field" menu.
    addableFields() {
      return this.optionalFields.filter((f) => !this.visibleOptionalFieldKeys.includes(f.key));
    },

    // Is the campaign's schema offering a `shortlink` field at all?
    hasShortlinkField() {
      return this.selectableFields.some((f) => f.key === "shortlink");
    },

    // File columns, as { header, index } pairs.
    availableColumns() {
      return this.fileHeaders.map((header, index) => ({ header, index }));
    },

    // Field keys currently assigned to a file column.
    mappedFieldKeys() {
      return Object.values(this.mapping).filter(Boolean);
    },

    // File column indices used by a merge's secondary slot. These are
    // "taken" too — they feed a field via concatenation.
    mergedColumnIndices() {
      return Object.values(this.merges)
        .map((m) => (m ? m.columnIndex : null))
        .filter((index) => index != null && index !== "");
    },

    // First selectable field whose type is "phone" — the one we'd prefix.
    phoneField() {
      return this.selectableFields.find((f) => f.type === "phone") || null;
    },

    // First selectable field that looks like a country (key, type or aliases).
    countryField() {
      return (
        this.selectableFields.find((f) => {
          if (f.key === "country" || f.type === "country") return true;
          return Array.isArray(f.aliases) && f.aliases.some((a) => /country/i.test(a));
        }) || null
      );
    },

    // True when a phone column is mapped — gates the phone-prefix block.
    phoneFieldMapped() {
      if (!this.phoneField) return false;
      return this.mappedFieldKeys.includes(this.phoneField.key);
    },

    // Country detected from the user's IP (set on login from the
    // `x-geo-ip-country` response header). Used as the default fallback.
    ipDefaultCountry() {
      const value = this.$store && this.$store.state.account.userCountry;
      return value ? String(value).toUpperCase() : "";
    },

    // Countries with calling codes, suitable for the default-country dropdown.
    phoneCountryOptions() {
      const supported = new Set(getCountries());
      return countryList
        .filter((c) => supported.has(c.iso_code))
        .map((c) => ({
          code: c.iso_code,
          name: c.name,
          flag: c.flag,
          callingCode: getCountryCallingCode(c.iso_code),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    // Field keys mapped to a column or given a non-empty fixed value.
    providedFieldKeys() {
      const constantKeys = Object.keys(this.constants).filter(
        (key) => String(this.constants[key] == null ? "" : this.constants[key]).trim() !== ""
      );
      return [...new Set([...this.mappedFieldKeys, ...constantKeys])];
    },

    // The ticked match fields the file can actually be compared on.
    activeMatchFields() {
      return this.matchFields.filter((key) => this.providedFieldKeys.includes(key));
    },

    // The dry run found duplicates and no action was chosen for them yet.
    needsDuplicateAction() {
      return (
        this.checkDuplicates &&
        !!this.preview &&
        Array.isArray(this.preview.duplicates) &&
        this.preview.duplicates.length > 0 &&
        !this.duplicateAction
      );
    },

    localErrors() {
      const errors = [];

      if (this.checkDuplicates && !this.activeMatchFields.length) {
        errors.push(
          this.$t(
            "import_teamsv2.dup_err_no_fields",
            "Tick at least one mapped field to compare for duplicates."
          )
        );
      }

      // Each required field must be mapped to a column or, when set globally,
      // carry a non-empty fixed value.
      this.requiredFields.forEach((field) => {
        if (this.isFieldUnfilled(field.key)) {
          errors.push(this.$t("import_teamsv2.err_required_missing", { field: field.label }));
        }
      });

      // Each optional field shown as a row must be mapped to a column or, when
      // set globally, carry a non-empty fixed value — otherwise removed.
      this.visibleOptionalFields.forEach((field) => {
        if (this.isConstantField(field.key)) {
          const value = this.constants[field.key];
          if (value == null || String(value).trim() === "") {
            errors.push(
              this.$t(
                "import_teamsv2.err_optional_no_value",
                "Set a fixed value for “{field}” or remove the field.",
                { field: field.label }
              )
            );
          }
        } else if (this.getFieldColumn(field.key) === "") {
          errors.push(this.$t("import_teamsv2.err_optional_unmapped", { field: field.label }));
        }
      });

      // Short links are produced by the switch — it must be on.
      if (this.hasShortlinkField && !this.autoGenerateShortlink) {
        errors.push(
          this.$t(
            "import_teamsv2.err_shortlink_switch",
            "Turn on “Auto-generate short links” — short links are required."
          )
        );
      }

      // A field both mapped to a column and given a fixed value.
      Object.keys(this.constants).forEach((key) => {
        if (this.constants[key] !== "" && this.mappedFieldKeys.includes(key)) {
          const field = this.selectableFields.find((f) => f.key === key);
          errors.push(
            this.$t("import_teamsv2.err_column_and_constant", {
              field: field ? field.label : key,
            })
          );
        }
      });

      return [...new Set(errors)];
    },
  },

  watch: {
    procID() {
      if (this.procID > 0 && this.interval == null) {
        this.interval = setInterval(this.checkProcProgress, 2000);
      }
    },

    // Any change to the mapping invalidates a previous dry-run preview.
    mapping: {
      deep: true,
      handler() {
        this.preview = null;
      },
    },
    constants: {
      deep: true,
      handler() {
        this.preview = null;
      },
    },

    visibleOptionalFieldKeys: {
      deep: true,
      handler() {
        this.preview = null;
      },
    },
    constantFieldKeys: {
      deep: true,
      handler() {
        this.preview = null;
      },
    },
    merges: {
      deep: true,
      handler() {
        this.preview = null;
      },
    },
    autoGenerateShortlink() {
      this.preview = null;
    },
    // Another set of match fields finds other duplicates, so the earlier
    // result and the action chosen for it no longer apply.
    checkDuplicates() {
      this.preview = null;
      this.duplicateAction = "";
    },
    matchFields() {
      this.preview = null;
      this.duplicateAction = "";
    },
    autoPhonePrefix() {
      this.preview = null;
    },
    defaultPhoneCountry() {
      this.preview = null;
    },
  },

  methods: {
    openModal() {
      this.$root.$emit("bv::show::modal", "importTeamsV2");
    },

    hideModal() {
      this.$root.$emit("bv::hide::modal", "importTeamsV2");
    },

    onShown() {
      if (!this.defaultPhoneCountry) {
        this.defaultPhoneCountry = this.ipDefaultCountry;
      }
      if (!this.fields.length) {
        this.fetchSchema();
      }
    },

    fetchSchema() {
      this.loadingSchema = true;
      this.schemaError = "";

      axios
        .get(
          urls.importTeamsV2Schema
            .replace(":orgId", this.orgId)
            .replace(":campaignId", this.resolvedCampaignId)
        )
        .then((response) => {
          this.fields = (response.data && response.data.fields) || [];
        })
        .catch((e) => {
          this.schemaError =
            (e.response && e.response.data && e.response.data.error) ||
            this.$t("import_teamsv2.schema_error", "Could not load the import fields");
        })
        .finally(() => {
          this.loadingSchema = false;
        });
    },

    /* ------------------------- file handling ------------------------ */

    onFileInputChange(e) {
      const file = e.target.files && e.target.files[0];
      if (file) {
        this.handleFile(file);
      }
    },

    onDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) {
        this.handleFile(file);
      }
    },

    handleFile(file) {
      const name = (file.name || "").toLowerCase();
      if (!name.endsWith(".xlsx") && !name.endsWith(".csv")) {
        this.fileError = this.$t(
          "import_teamsv2.bad_file_type",
          "Please upload an .xlsx or .csv file"
        );
        return;
      }

      this.fileError = "";
      this.readingFile = true;
      this.file = file;
      this.fileName = file.name;

      readSpreadsheetAllRows(file)
        .then(({ headers, rows }) => {
          this.fileHeaders = headers;
          this.fileRows = rows;
          this.buildInitialMapping(headers);
        })
        .catch((e) => {
          this.fileHeaders = [];
          this.fileRows = [];
          this.file = null;
          this.fileName = "";
          this.fileError =
            e.message || this.$t("import_teamsv2.read_error", "Could not read the file");
        })
        .finally(() => {
          this.readingFile = false;
        });
    },

    // Pre-fill the mapping by matching headers against field aliases/labels.
    // Optional fields only appear as a row when a header matched them.
    // Headers that start with `x_` are auto-registered as custom extra-data keys.
    buildInitialMapping(headers) {
      const mapping = {};
      const usedFields = {};
      const visibleOptional = [];
      const customKeys = [];

      headers.forEach((header, index) => {
        mapping[index] = "";

        const raw = typeof header === "string" ? header.trim() : "";
        if (raw.startsWith("x_") && this.isValidExtraDataKey(raw.slice(2))) {
          if (!customKeys.includes(raw) && !usedFields[raw]) {
            customKeys.push(raw);
            mapping[index] = raw;
            usedFields[raw] = true;
            visibleOptional.push(raw);
          }
          return;
        }

        const match = this.suggestField(header, usedFields);
        if (match) {
          mapping[index] = match;
          usedFields[match] = true;

          const field = this.fields.find((f) => f.key === match);
          if (field && !field.required && !visibleOptional.includes(match)) {
            visibleOptional.push(match);
          }
        }
      });

      this.customFieldKeys = customKeys;

      const constants = {};
      this.selectableFields.forEach((f) => {
        constants[f.key] = "";
      });

      this.mapping = mapping;
      this.constants = constants;
      this.visibleOptionalFieldKeys = visibleOptional;
      this.preview = null;
    },

    isValidExtraDataKey(key) {
      return /^[a-zA-Z0-9_-]{1,64}$/.test(key);
    },

    suggestField(header, usedFields) {
      const normalized = this.normalize(header);
      if (!normalized) {
        return "";
      }

      const match = this.selectableFields.find((field) => {
        // `shortlink` is never auto-mapped — it is the switch's job.
        if (field.key === "shortlink" || usedFields[field.key]) {
          return false;
        }
        const candidates = [field.label, field.key, ...(field.aliases || [])];
        return candidates.some((c) => this.normalize(c) === normalized);
      });

      return match ? match.key : "";
    },

    normalize(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    },

    // Which file-column index currently feeds a field ("" if none).
    getFieldColumn(fieldKey) {
      const index = Object.keys(this.mapping).find((idx) => this.mapping[idx] === fieldKey);
      return index === undefined ? "" : Number(index);
    },

    // Assign (or clear) the file column that feeds a field.
    setFieldColumn(fieldKey, columnValue) {
      Object.keys(this.mapping).forEach((idx) => {
        if (this.mapping[idx] === fieldKey) {
          this.mapping[idx] = "";
        }
      });
      if (columnValue === "") {
        // Dropping the primary column also drops any secondary merge — the
        // merge has no anchor to attach to.
        this.removeMerge(fieldKey);
      } else {
        this.mapping[Number(columnValue)] = fieldKey;
      }
    },

    // A column is "taken" if it already feeds a different field, either as
    // a primary mapping or as a merge's secondary column.
    isColumnTaken(columnIndex, exceptFieldKey) {
      const mapped = this.mapping[columnIndex];
      if (mapped && mapped !== exceptFieldKey) return true;
      return Object.keys(this.merges).some((fieldKey) => {
        if (fieldKey === exceptFieldKey) return false;
        const merge = this.merges[fieldKey];
        return merge && Number(merge.columnIndex) === Number(columnIndex);
      });
    },

    // Stricter check used for merge-slot dropdowns: a field can't merge with
    // its own primary column, and other fields' primaries/merges still block.
    isColumnTakenForMerge(columnIndex, fieldKey) {
      if (this.mapping[columnIndex]) return true;
      return Object.keys(this.merges).some((key) => {
        if (key === fieldKey) return false;
        const merge = this.merges[key];
        return merge && Number(merge.columnIndex) === Number(columnIndex);
      });
    },

    /* ------------------------- column merging ------------------------ */

    // True when a second column has been attached to this field.
    hasMerge(fieldKey) {
      return Boolean(this.merges[fieldKey]);
    },

    // Read the secondary column index for a field ("" if none).
    getMergeColumn(fieldKey) {
      const merge = this.merges[fieldKey];
      return merge ? merge.columnIndex : "";
    },

    // Read the merge separator (defaults to a space).
    getMergeSeparator(fieldKey) {
      const merge = this.merges[fieldKey];
      return merge && merge.separator != null ? merge.separator : " ";
    },

    // Start merging a second column into this field. Default separator: " ".
    startMerge(fieldKey) {
      if (this.merges[fieldKey]) return;
      this.$set(this.merges, fieldKey, { columnIndex: "", separator: " " });
    },

    // Update the merge's secondary column.
    setMergeColumn(fieldKey, columnValue) {
      if (!this.merges[fieldKey]) {
        this.startMerge(fieldKey);
      }
      this.$set(this.merges, fieldKey, {
        ...this.merges[fieldKey],
        columnIndex: columnValue === "" ? "" : Number(columnValue),
      });
    },

    // Update the merge separator.
    setMergeSeparator(fieldKey, separator) {
      if (!this.merges[fieldKey]) {
        this.startMerge(fieldKey);
      }
      this.$set(this.merges, fieldKey, {
        ...this.merges[fieldKey],
        separator,
      });
    },

    // Drop the merge entirely for this field.
    removeMerge(fieldKey) {
      if (this.merges[fieldKey]) {
        this.$delete(this.merges, fieldKey);
      }
    },

    // Show an optional field as a mapping row.
    addOptionalField(fieldKey) {
      if (!this.visibleOptionalFieldKeys.includes(fieldKey)) {
        this.visibleOptionalFieldKeys.push(fieldKey);
      }
    },

    // Remove an optional field row and clear its column assignment, merge and
    // any fixed value. Custom fields (x_*) are also removed from the
    // virtual-field list, so they fully disappear unless the user re-adds them.
    removeOptionalField(fieldKey) {
      this.visibleOptionalFieldKeys = this.visibleOptionalFieldKeys.filter(
        (key) => key !== fieldKey
      );
      this.constantFieldKeys = this.constantFieldKeys.filter((key) => key !== fieldKey);
      this.setFieldColumn(fieldKey, "");
      this.removeMerge(fieldKey);
      this.$delete(this.constants, fieldKey);
      if (this.customFieldKeys.includes(fieldKey)) {
        this.customFieldKeys = this.customFieldKeys.filter((key) => key !== fieldKey);
      }
    },

    // Is this field currently set to one fixed value for every row?
    isConstantField(fieldKey) {
      return this.constantFieldKeys.includes(fieldKey);
    },

    // A field is "unfilled" when it is neither mapped to a column nor — in
    // fixed-value mode — given a non-empty value. Used to flag required rows.
    isFieldUnfilled(fieldKey) {
      if (this.isConstantField(fieldKey)) {
        const value = this.constants[fieldKey];
        return value == null || String(value).trim() === "";
      }
      return this.getFieldColumn(fieldKey) === "";
    },

    // Toggle a field between "map a file column" and "set one fixed value for
    // every row". The two are mutually exclusive, so switching clears the other.
    toggleConstantField(fieldKey) {
      if (this.isConstantField(fieldKey)) {
        this.constantFieldKeys = this.constantFieldKeys.filter((key) => key !== fieldKey);
        this.$set(this.constants, fieldKey, "");
        return;
      }
      this.setFieldColumn(fieldKey, "");
      this.removeMerge(fieldKey);
      if (this.constants[fieldKey] == null) {
        this.$set(this.constants, fieldKey, "");
      }
      this.constantFieldKeys.push(fieldKey);
    },

    /* --------------------- custom extra-data fields ------------------- */

    startCustomFieldPrompt() {
      this.customFieldPromptKey = "";
      this.customFieldPromptError = "";
      this.customFieldPromptOpen = true;
      this.$nextTick(() => {
        const input = this.$refs.customFieldPromptInput;
        if (input && input.focus) input.focus();
      });
    },

    cancelCustomFieldPrompt() {
      this.customFieldPromptOpen = false;
      this.customFieldPromptKey = "";
      this.customFieldPromptError = "";
    },

    confirmCustomFieldPrompt() {
      const trimmed = (this.customFieldPromptKey || "").trim();
      if (!this.isValidExtraDataKey(trimmed)) {
        this.customFieldPromptError = this.$t(
          "import_teamsv2.custom_field_invalid_key",
          "Key must match [a-zA-Z0-9_-] and be 1-64 chars"
        );
        return;
      }

      const fullKey = "x_" + trimmed;

      if (this.customFieldKeys.includes(fullKey)) {
        this.customFieldPromptError = this.$t(
          "import_teamsv2.custom_field_duplicate",
          "A custom field with this key already exists"
        );
        return;
      }
      if (this.fields.some((f) => f.key === fullKey)) {
        this.customFieldPromptError = this.$t(
          "import_teamsv2.custom_field_collides_with_known",
          "This key conflicts with a built-in field"
        );
        return;
      }

      this.customFieldKeys.push(fullKey);
      this.$set(this.constants, fullKey, "");
      this.visibleOptionalFieldKeys.push(fullKey);

      this.customFieldPromptOpen = false;
      this.customFieldPromptKey = "";
      this.customFieldPromptError = "";
    },

    /* -----------------------  mappingpayload ------------------------ */

    // Ordered list of fields that are mapped to a column, sorted by column
    // index. Used both when rewriting the file (one column per field, in
    // this order) and when emitting the post-transform mapping payload.
    orderedMappedFields() {
      return Object.keys(this.mapping)
        .map(Number)
        .sort((a, b) => a - b)
        .filter((idx) => this.mapping[idx])
        .map((idx) => ({ columnIndex: idx, fieldKey: this.mapping[idx] }));
    },

    // We rewrite the file client-side only when at least one transformation
    // is configured — otherwise we ship the original bytes untouched. The
    // phone-prefix transform runs only when the user opts in and a phone
    // field is mapped.
    shouldTransformFile() {
      if (this.autoPhonePrefix && this.phoneFieldMapped) return true;
      return Object.keys(this.merges).some((key) => {
        const merge = this.merges[key];
        return merge && merge.columnIndex !== "";
      });
    },

    buildMappingPayload(columnsOverride) {
      const columns = columnsOverride || {};
      if (!columnsOverride) {
        Object.keys(this.mapping).forEach((index) => {
          if (this.mapping[index]) {
            columns[index] = this.mapping[index];
          }
        });
      }

      const constants = {};
      Object.keys(this.constants).forEach((key) => {
        const field = this.fields.find((f) => f.key === key);
        const raw = this.constants[key];

        // Boolean fields go to the server as a real bool, never a string.
        if (field && field.type === "bool") {
          if (raw === "true" || raw === "false") {
            constants[key] = raw === "true";
          }
          return;
        }

        const value = String(raw == null ? "" : raw).trim();
        if (value !== "") {
          constants[key] = value;
        }
      });

      return { columns, constants };
    },

    // True only when the API explicitly allows a single fixed value for all
    // teams. Fields that must be unique per team are not flagged.
    fieldSupportsConstant(field) {
      return field.supports_constant === true || field.supportsConstant === true;
    },

    // HTML input type to use for a field's fixed-value control.
    fixedValueInputType(type) {
      if (type === "int") return "number";
      if (type === "email") return "email";
      if (type === "phone") return "tel";
      return "text";
    },

    /* --------------------- client-side transforms -------------------- */

    // Apply phone-prefix + merge transforms to in-memory rows and return a
    // fresh CSV File. The output has one column per mapped Charidy field,
    // in `orderedMappedFields` order — and the caller must emit a matching
    // mapping (0 → fieldKey1, 1 → fieldKey2, …) so the server lines them up.
    buildTransformedFile() {
      const fields = this.orderedMappedFields();
      const headerRow = fields.map(({ fieldKey }) => fieldKey);
      const dataRows = this.fileRows.map((row) =>
        fields.map(({ fieldKey, columnIndex }) =>
          this.computeTransformedCell(row, fieldKey, columnIndex)
        )
      );
      const csv = buildCsv([headerRow, ...dataRows]);

      const baseName = ((this.file && this.file.name) || "import.csv").replace(
        /\.(csv|xlsx)$/i,
        ""
      );
      // Prepend a UTF-8 BOM so Excel detects unicode correctly if anyone
      // opens this re-export later.
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
      return new File([blob], baseName + "-transformed.csv", {
        type: "text/csv;charset=utf-8",
      });
    },

    computeTransformedCell(row, fieldKey, columnIndex) {
      let value = row[columnIndex] != null ? String(row[columnIndex]) : "";

      // Merge a second column if configured.
      const merge = this.merges[fieldKey];
      if (merge && merge.columnIndex !== "" && merge.columnIndex != null) {
        const secondary = row[merge.columnIndex] != null ? String(row[merge.columnIndex]) : "";
        const sep = merge.separator != null ? merge.separator : " ";
        value = this.joinMergeValues(value, secondary, sep);
      }

      if (
        this.autoPhonePrefix &&
        this.phoneField &&
        fieldKey === this.phoneField.key &&
        value.trim() !== ""
      ) {
        value = this.applyPhonePrefix(value, row);
      }

      return value;
    },

    joinMergeValues(a, b, sep) {
      const left = String(a == null ? "" : a).trim();
      const right = String(b == null ? "" : b).trim();
      if (!left) return right;
      if (!right) return left;
      return left + sep + right;
    },

    // Add the country calling code (with a leading "+") to a phone. The code
    // comes from the mapped country column, falling back to the chosen default
    // country. A single leading 0 is dropped before the code is added. No
    // length or validity checks are performed — the value is taken as given.
    applyPhonePrefix(phone, row) {
      const raw = String(phone == null ? "" : phone).trim();
      if (!raw) return phone;
      // Already international — leave it untouched.
      if (raw.startsWith("+")) return raw;

      // Find the country for this row: the mapped country column wins,
      // with the user's chosen default as fallback.
      let country = "";
      if (this.countryField) {
        const idx = this.getFieldColumn(this.countryField.key);
        if (idx !== "") {
          country = row[idx] != null ? String(row[idx]) : "";
        }
      }
      if (!country.trim()) country = this.defaultPhoneCountry;

      const iso = this.resolveCountryIso(country);
      let callingCode = "";
      if (iso) {
        try {
          callingCode = String(getCountryCallingCode(iso));
        } catch (e) {
          callingCode = "";
        }
      }
      // No country to work with — leave the number as the user provided it.
      if (!callingCode) return phone;

      // Keep digits only (drops separators like spaces and dashes).
      let digits = raw.replace(/[^\d]/g, "");
      // Already carries this country's calling code — just add the "+".
      if (digits.startsWith(callingCode)) return "+" + digits;
      // Drop a single leading 0, then prepend the country code.
      if (digits.startsWith("0")) digits = digits.slice(1);
      return "+" + callingCode + digits;
    },

    // Best-effort country → ISO-2 lookup: accept "IL"/"il" or "Israel".
    resolveCountryIso(value) {
      const raw = String(value || "").trim();
      if (!raw) return "";

      if (raw.length === 2) {
        const upper = raw.toUpperCase();
        try {
          getCountryCallingCode(upper);
          return upper;
        } catch (e) {
          // fall through
        }
      }

      const lower = raw.toLowerCase();
      const match = countryList.find((c) => c.name.toLowerCase() === lower);
      return match ? match.iso_code : "";
    },

    buildFormData(isDryRun, uploadFile, mappingPayload) {
      const data = new FormData();
      data.append("file", uploadFile);
      data.append("mapping", JSON.stringify(mappingPayload));
      if (isDryRun) {
        data.append("dry_run", "1");
      }
      if (this.hasShortlinkField && this.autoGenerateShortlink) {
        data.append("auto_generate_shortlink", "1");
      }
      if (this.sendTeamCreationEmail) {
        data.append("override_send_team_creation_email", this.sendTeamCreationEmail);
      }
      if (this.sendNewLinkedTeamAccountEmail) {
        data.append(
          "override_send_new_linked_team_account_email",
          this.sendNewLinkedTeamAccountEmail
        );
      }
      if (this.checkDuplicates) {
        data.append("match_fields", this.activeMatchFields.join(","));
        // a dry run without an action shows the file as "create"; the import
        // must name one, and "create" is right when nothing was found
        const action = this.duplicateAction || (isDryRun ? "" : "create");
        if (action) {
          data.append("duplicate_action", action);
        }
      }
      return data;
    },

    // Returns { file, mapping } — the file to upload (transformed when any
    // client-side transform is configured) and the matching mapping payload.
    prepareUpload() {
      if (!this.shouldTransformFile()) {
        return {
          file: this.file,
          mapping: this.buildMappingPayload(),
        };
      }

      const transformedFile = this.buildTransformedFile();
      const columns = {};
      this.orderedMappedFields().forEach((f, idx) => {
        columns[idx] = f.fieldKey;
      });
      return {
        file: transformedFile,
        mapping: this.buildMappingPayload(columns),
      };
    },

    importUrl() {
      return (this.checkDuplicates ? urls.importTeamsV2Dedupe : urls.importTeamsV2)
        .replace(":orgId", this.orgId)
        .replace(":campaignId", this.resolvedCampaignId);
    },

    /* --------------------------- dry-run ---------------------------- */

    runDryRun() {
      if (this.localErrors.length || !this.file) {
        return;
      }

      this.busy = true;
      this.generalError = "";

      const { file, mapping } = this.prepareUpload();

      axios
        .post(this.importUrl(), this.buildFormData(true, file, mapping))
        .then((response) => {
          this.preview = {
            valid: !!response.data.valid,
            errors: response.data.errors || [],
            rows_parsed: response.data.rows_parsed || 0,
            new_teams: response.data.new_teams || 0,
            duplicates: this.checkDuplicates ? response.data.duplicates || [] : null,
            skipped_rows: response.data.skipped_rows || 0,
          };
        })
        .catch((e) => {
          this.handleRequestError(e);
        })
        .finally(() => {
          this.busy = false;
        });
    },

    /* -------------------------- real import ------------------------- */

    runImport() {
      if (!this.preview || !this.preview.valid || !this.file) {
        return;
      }

      this.busy = true;
      this.generalError = "";
      this.lastValidation = this.preview;

      const { file, mapping } = this.prepareUpload();

      axios
        .post(this.importUrl(), this.buildFormData(false, file, mapping))
        .then((response) => {
          const attrs = response.data.data.attributes;
          this.importGroup = attrs.group;
          this.importCounts =
            attrs.created === undefined
              ? null
              : { created: attrs.created, updated: attrs.updated, skipped: attrs.skipped };
          this.progress = 0;
          this.step = "importing";
          this.procID =
            (attrs.proc_session && (attrs.proc_session.ID || attrs.proc_session.id)) || 0;
        })
        .catch((e) => {
          this.handleRequestError(e);
        })
        .finally(() => {
          this.busy = false;
        });
    },

    // A new duplicate action changes what the file imports, so the shown
    // result is validated again with it.
    onDuplicateActionChange(action) {
      this.duplicateAction = action;
      if (this.preview && !this.busy) {
        this.runDryRun();
      }
    },

    checkProcProgress() {
      if (this.procID === 0) {
        return;
      }

      axios
        .get(urls.procProgressTracker.replace(":oid", this.orgId).replace(":id", this.procID))
        .then((response) => {
          const attr = response.data.data.attributes;
          this.progress = attr.progress;

          if (attr.finished) {
            clearInterval(this.interval);
            this.interval = null;
            this.step = 3;
            this.$emit("update-team-list");
            this.$parent && this.$parent.loadNewPageTeams && this.$parent.loadNewPageTeams();

            if (this.afterSend === "sms") {
              this.$emit("send-sms", { importGroup: this.importGroup });
              this.hideModal();
            }
            if (this.afterSend === "email") {
              this.$emit("send-email", { importGroup: this.importGroup });
              this.hideModal();
            }
          }
        })
        .catch((e) => {
          clearInterval(this.interval);
          this.interval = null;
          this.handleRequestError(e);
          this.step = 2;
        });
    },

    handleRequestError(e) {
      const data = e.response && e.response.data;
      if (data && data.error) {
        this.generalError = data.error;
      } else {
        this.generalError = this.$t("template.error_default", "Something went wrong");
      }
      // Per-row detail, when the server provides it.
      if (data && Array.isArray(data.errors) && data.errors.length) {
        this.preview = {
          valid: false,
          errors: data.errors,
          rows_parsed: 0,
          new_teams: 0,
        };
      }
    },

    clearData() {
      this.step = 1;
      this.file = null;
      this.fileName = "";
      this.fileHeaders = [];
      this.fileRows = [];
      this.readingFile = false;
      this.fileError = "";
      this.dragOver = false;
      this.mapping = {};
      this.constants = {};
      this.merges = {};
      this.visibleOptionalFieldKeys = [];
      this.constantFieldKeys = [];
      this.customFieldKeys = [];
      this.autoPhonePrefix = false;
      this.defaultPhoneCountry = this.ipDefaultCountry;
      this.customFieldPromptOpen = false;
      this.customFieldPromptKey = "";
      this.customFieldPromptError = "";
      this.autoGenerateShortlink = true;
      this.sendTeamCreationEmail = "";
      this.sendNewLinkedTeamAccountEmail = "";
      this.afterSend = null;
      this.checkDuplicates = false;
      this.matchFields = ["name"];
      this.duplicateAction = "";
      this.importCounts = null;
      this.busy = false;
      this.preview = null;
      this.lastValidation = null;
      this.generalError = "";
      this.procID = 0;
      this.importGroup = null;
      this.progress = 0;
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
  },

  beforeDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  },
};
</script>

<style scoped lang="scss">
.import-v2 {
  padding: 16px 4px;
  min-height: 220px;
}

.import-v2__step-badge {
  position: absolute;
  top: 22px;
  right: 56px;
  font-size: 13px;
  color: #7f8b98;
}

.import-v2__center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
  padding: 24px 0;
}

.import-v2__title {
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 4px;
}

.import-v2__subtitle {
  text-align: center;
  color: #7f8b98;
  margin-bottom: 20px;
}

/* ---- Step 1: dropzone ---- */
.import-v2__dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 36px 16px;
  border: 2px dashed #b8c2cc;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;

  &--active,
  &:hover {
    border-color: #107598;
    background-color: #f3f9fb;
  }
}

.import-v2__dropzone-title {
  font-weight: 600;
  margin: 4px 0 0;
}

.import-v2__dropzone-hint {
  color: #7f8b98;
  font-size: 13px;
  margin: 0;
}

.import-v2__file-input {
  display: none;
}

/* ---- Step 2: mapping ---- */
.import-v2__map-head {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #7f8b98;
  margin-bottom: 8px;
  padding: 0 4px;
}

.import-v2__map-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.import-v2__map-col {
  flex: 1;
  padding: 10px 12px;
  background: #f5f7f9;
  border: 1px solid #e1e6ea;
  border-radius: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.import-v2__map-arrow {
  color: #b8c2cc;
}

.import-v2__select,
.import-v2__input {
  flex: 1;
  height: 40px;
  padding: 0 10px;
  border: 1px solid #88bacc;
  border-radius: 6px;
  background: #fff;
}

.import-v2__section {
  margin-top: 18px;
}

.import-v2__section-title {
  font-weight: 600;
  margin-bottom: 2px;
}

.import-v2__section-hint {
  color: #7f8b98;
  font-size: 13px;
  margin-bottom: 10px;
}

.import-v2__add {
  margin-top: 4px;
}

.import-v2__map-row--required .import-v2__map-col {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #fff;
  border-color: #88bacc;
}

.import-v2__map-row--missing .import-v2__map-col {
  border-color: #d9534f;
}

.import-v2__required-badge {
  flex: none;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #d9534f;
  background: #fdecec;
  border-radius: 10px;
  padding: 2px 8px;
}

.import-v2__trash {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e1e6ea;
  border-radius: 6px;
  background: #fff;
  color: #7f8b98;
  cursor: pointer;

  &:hover {
    border-color: #d9534f;
    color: #d9534f;
  }
}

.import-v2__const-toggle {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #88bacc;
  border-radius: 6px;
  background: #fff;
  color: #107598;
  cursor: pointer;

  &:hover {
    background: #f3f9fb;
  }

  &--active {
    background: #107598;
    border-color: #107598;
    color: #fff;
  }
}

.import-v2__auto-shortlink {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e1e6ea;

  .import-v2__hint {
    margin: 4px 0 0;
    font-size: 13px;
  }
}

.import-v2__preview {
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #e1e6ea;

  &.is-valid {
    background: #effaf4;
    border-color: #b6e3cd;
  }

  &.is-invalid {
    background: #fdf2f2;
    border-color: #f3c9c9;
  }
}

.import-v2__preview-head {
  font-weight: 600;
  margin-bottom: 4px;
}

.import-v2__error {
  color: #d9534f;
}

.import-v2__error-list {
  margin-top: 8px;
  padding-left: 18px;
  color: #d9534f;
  font-size: 13px;
}

.import-v2__hint {
  margin-top: 12px;
  color: #7f8b98;
}

.import-v2__footer {
  display: flex;
  gap: 8px;
}

.import-v2__custom-prompt {
  margin-top: 10px;
  padding: 10px 12px;
  background: #f5f7f9;
  border: 1px solid #e1e6ea;
  border-radius: 6px;
}

.import-v2__custom-prompt-label {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  display: block;
}

.import-v2__custom-prompt-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.import-v2__custom-prompt-prefix {
  font-family: monospace;
  color: #7f8b98;
  font-weight: 600;
}

.import-v2__map-group {
  margin-bottom: 8px;
}

.import-v2__map-group .import-v2__map-row {
  margin-bottom: 0;
}

.import-v2__merge-toggle {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #88bacc;
  border-radius: 6px;
  background: #fff;
  color: #107598;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: #f3f9fb;
  }
}

.import-v2__merge-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 0 28px;
  padding-left: 12px;
  border-left: 2px solid #e1e6ea;
}

.import-v2__merge-label {
  color: #7f8b98;
  font-size: 13px;
  flex: none;
}

.import-v2__merge-sep {
  flex: none;
  width: 70px;
  height: 36px;
  padding: 0 8px;
  border: 1px solid #88bacc;
  border-radius: 6px;
  background: #fff;
  text-align: center;
}

.import-v2__phone-default {
  margin-top: 8px;
}

.import-v2__phone-default-label {
  display: block;
  font-size: 13px;
  color: #7f8b98;
  margin-bottom: 4px;
}

.import-v2__warn {
  margin-top: 10px;
  padding: 8px 10px;
  font-size: 13px;
  color: #8a6d3b;
  background: #fcf8e3;
  border: 1px solid #faebcc;
  border-radius: 6px;
}
</style>
